import { useMemo, useState } from 'react';
import { BREEDS } from './breeds';
import type {
  FetchDefaultSelection,
  FetchPetFormValues,
  FetchQuoteResult,
  FetchSelectInput,
  RateMatrixCoPay,
  RateMatrixEntry,
} from './types';

type PlanSelection = Omit<FetchSelectInput, 'cartId' | 'prospectPetId'>;

export function money(n?: number, fractionDigits = 0): string {
  if (n == null) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(n);
}

// Fetch asked us to stop offering payouts below $5,000 and deductibles above
// $1,000, and to cut the remaining optionality down to these values.
const ALLOWED_LIMITS = [5000, 7500, 10000, 15000, 25000, 100000];
const ALLOWED_DEDUCTIBLES = [250, 300, 400, 500, 700, 1000];
const MIN_LIMIT = 5000;
const MAX_DEDUCTIBLE = 1000;
const HIDDEN_REIMBURSEMENT = 60;

// Fetch's top payout tier arrives as the string "Unlimited"; they asked for it to
// read as the cap it actually is. Display only -- "Unlimited" stays the value we
// send back, since that is the key their rate matrix and select call use.
const UNLIMITED_AMOUNT = 100000;

function isUnlimited(amount: number | string): boolean {
  return (
    typeof amount === 'string' && amount.trim().toLowerCase() === 'unlimited'
  );
}

// Anything else non-numeric renders as itself rather than going through
// Intl.NumberFormat, which would turn it into "$NaN".
export function amountLabel(amount: number | string): string {
  if (isUnlimited(amount)) return money(UNLIMITED_AMOUNT);
  const numeric = Number(amount);
  return Number.isNaN(numeric) ? String(amount) : money(numeric);
}

// Fetch returns the payout tiers unsorted -- ...25000, Unlimited, 6000, 7000... --
// so the dropdown has to order them or the list reads out of sequence.
function limitRank(amount: number | string): number {
  const numeric = Number(amount);
  return Number.isNaN(numeric) ? UNLIMITED_AMOUNT : numeric;
}

// The allow-lists are Fetch's recommendation; the bounds are their hard rule.
// Narrowing to the recommended values only when something survives keeps an
// unexpected amount from emptying the dropdown while still honouring the bound.
function narrow(
  amounts: (number | string)[],
  allowed: number[],
  inBounds: (amount: number) => boolean,
): (amount: number | string) => boolean {
  const bounded = amounts
    .map(Number)
    .filter((n) => !Number.isNaN(n) && inBounds(n));
  const recommended = bounded.filter((a) => allowed.includes(a));
  const keep = new Set(recommended.length > 0 ? recommended : bounded);
  // A non-numeric amount is one of Fetch's open-ended tiers ("Unlimited"). It is
  // the top of the range by definition, so there is no bound to test it against
  // and it always passes.
  return (amount) => Number.isNaN(Number(amount)) || keep.has(Number(amount));
}

// 60% is dropped unless it is all Fetch offered, in which case it is the
// default by definition.
function filterCoPay(coPay: RateMatrixCoPay[]): RateMatrixCoPay[] {
  const kept = coPay.filter(
    (c) => Number(c.percentage) !== HIDDEN_REIMBURSEMENT,
  );
  return kept.length > 0 ? kept : coPay;
}

function filterMatrix(matrix: RateMatrixEntry[]): RateMatrixEntry[] {
  const keepLimit = narrow(
    matrix.map((entry) => entry.amount),
    ALLOWED_LIMITS,
    (amount) => amount >= MIN_LIMIT,
  );

  return matrix
    .filter((entry) => keepLimit(entry.amount))
    .map((entry) => {
      const deductibles = entry.deductible ?? [];
      const keepDeductible = narrow(
        deductibles.map((d) => d.amount),
        ALLOWED_DEDUCTIBLES,
        (amount) => amount <= MAX_DEDUCTIBLE,
      );
      return {
        ...entry,
        deductible: deductibles
          .filter((d) => keepDeductible(d.amount))
          .map((d) => ({ ...d, coPay: filterCoPay(d.coPay ?? []) }))
          .filter((d) => d.coPay.length > 0),
      };
    })
    .filter((entry) => entry.deductible.length > 0)
    .sort((a, b) => limitRank(a.amount) - limitRank(b.amount));
}

function ageSummary(age: string): string {
  return age === '0' ? 'under 1 year old' : `${age} year old`;
}

function breedName(pet: FetchPetFormValues | null): string {
  if (!pet) return 'pet';
  return BREEDS[pet.species].find((b) => b.id === pet.breedId)?.name ?? 'pet';
}

// Opens on Fetch's recommendation instead of whatever sorts first, which is how
// every shopper ended up looking at $5,000/$250/70%. Each level falls back to the
// first option on its own, so a recommendation our filters dropped still lands
// somewhere sensible rather than losing the levels below it too.
function defaultIndices(
  matrix: RateMatrixEntry[],
  selection: FetchDefaultSelection | null | undefined,
) {
  const fallback = { limitIdx: 0, dedIdx: 0, coPayIdx: 0 };
  if (!selection) return fallback;

  const limitIdx = matrix.findIndex(
    (entry) => String(entry.amount) === selection.limit,
  );
  if (limitIdx < 0) return fallback;

  const deductibles = matrix[limitIdx].deductible ?? [];
  const dedIdx = deductibles.findIndex(
    (d) => String(d.amount) === selection.deductible,
  );
  if (dedIdx < 0) return { limitIdx, dedIdx: 0, coPayIdx: 0 };

  const coPayIdx = (deductibles[dedIdx].coPay ?? []).findIndex(
    (c) => String(c.percentage) === selection.reimbursement,
  );
  return { limitIdx, dedIdx, coPayIdx: coPayIdx < 0 ? 0 : coPayIdx };
}

export function usePlanSelect(
  quote: FetchQuoteResult,
  petInfo: FetchPetFormValues | null,
) {
  const matrix = useMemo(() => filterMatrix(quote.rateMatrix ?? []), [quote]);
  const initial = useMemo(
    () => defaultIndices(matrix, quote.defaultSelection),
    [matrix, quote.defaultSelection],
  );

  const [limitIdx, setLimitIdx] = useState(initial.limitIdx);
  const [dedIdx, setDedIdx] = useState(initial.dedIdx);
  const [coPayIdx, setCoPayIdx] = useState(initial.coPayIdx);

  const setLimit = (i: number) => {
    setLimitIdx(i);
    setDedIdx(0);
    setCoPayIdx(0);
  };
  const setDed = (i: number) => {
    setDedIdx(i);
    setCoPayIdx(0);
  };
  const setCoPay = (i: number) => setCoPayIdx(i);

  const entry = matrix[limitIdx];
  const ded = entry?.deductible?.[dedIdx];
  const coPay = ded?.coPay?.[coPayIdx];
  const monthly =
    coPay?.discountTaxAndServicePremium?.monthly ??
    coPay?.discountPremium?.monthly ??
    quote.monthlyPrice?.amount;

  const petName = petInfo?.petName || 'Your pet';
  const summary = petInfo
    ? `${ageSummary(petInfo.age)} ${breedName(petInfo)}, ${petInfo.petName}`
    : '';

  const canSubmit = !!(entry && ded && coPay);

  const buildSelection = (): PlanSelection | null => {
    if (!entry || !ded || !coPay) return null;
    return {
      limit: String(entry.amount),
      deductible: String(ded.amount),
      reimbursement: String(coPay.percentage),
      billingFrequency: quote.defaultSelection?.billingFrequency ?? 'monthly',
    };
  };

  return {
    matrix,
    limitIdx,
    dedIdx,
    coPayIdx,
    setLimit,
    setDed,
    setCoPay,
    entry,
    ded,
    coPay,
    monthly,
    petName,
    summary,
    canSubmit,
    buildSelection,
  };
}
